
import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { TrainingSession, TrainingFormValues, TimePeriod } from '../types';
import { usePermissions } from '@/contexts/unified-permissions';
import { useSessionOperations } from './hooks/useSessionOperations';
import { useSessionQueries } from './hooks/useSessionQueries';
import {
  TrainingJournalContext,
  type TrainingJournalContextProps,
} from './journal-context';

export const TrainingJournalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = usePermissions();
  const [sessions, setSessions] = useState<TrainingSession[] | null>(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>('month');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { 
    addSession: createSession, 
    editSession: updateSession, 
    deleteSession: removeSession 
  } = useSessionOperations();
  
  const { 
    fetchSessions 
  } = useSessionQueries();

  // Remove memoizedFetchSessions to prevent circular dependencies

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setSessions(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call fetchSessions directly to avoid circular dependency
      const fetchedSessions = await fetchSessions(user.id);
      setSessions(fetchedSessions);
    } catch (err: unknown) {
      console.error('Error loading sessions:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load training sessions');
      }
      setSessions(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id, fetchSessions]);

  // Load sessions whenever authentication state or user ID changes
  // Removed loadSessions from dependencies to prevent infinite loops
  useEffect(() => {
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);
  
  const addSession = async (session: TrainingFormValues): Promise<TrainingSession | null> => {
    try {
      const newSession = await createSession(session);
      if (newSession) {
        // Prepend new session to the beginning of the array to match database ordering (newest first)
        setSessions((prevSessions: TrainingSession[] | null) => [newSession, ...(prevSessions || [])]);
        return newSession;
      }
      return null;
    } catch (err) {
      console.error("Error adding session:", err);
      throw err;
    }
  };

  const editSession = async (id: string, updates: TrainingFormValues): Promise<TrainingSession | null> => {
    try {
      const updatedSession = await updateSession(id, updates);
      if (updatedSession) {
        setSessions((prevSessions: TrainingSession[] | null) =>
          (prevSessions || []).map((session: TrainingSession) => (session.id === id ? updatedSession : session))
        );
        return updatedSession;
      }
      return null;
    } catch (err) {
      console.error("Error editing session:", err);
      throw err;
    }
  };

  const deleteSession = async (id: string): Promise<boolean> => {
    try {
      const success = await removeSession(id);
      if (success) {
        setSessions((prevSessions: TrainingSession[] | null) => (prevSessions || []).filter((session: TrainingSession) => session.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error deleting session:", err);
      return false;
    }
  };
  
  const refreshSessions = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  const value: TrainingJournalContextProps = {
    sessions,
    selectedTimePeriod,
    addSession,
    editSession,
    deleteSession,
    isLoading,
    error,
    refreshSessions,
    setSelectedTimePeriod
  };

  return (
    <TrainingJournalContext.Provider value={value}>
      {children}
    </TrainingJournalContext.Provider>
  );
};

